import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchOpenAIChatCompletion } from '../lib/openaiClient';
import { useAuth } from '../contexts/AuthContext';
import './EssayBuilder.css';
import hbsLogo from '../assets/HBS_LOGO.png';
import stanfordLogo from '../assets/STANFORD_LOGO.png';
import whartonLogo from '../assets/WHARTON_LOGO.png';
import columbiaLogo from '../assets/COLUMBIA_LOGO.png';
import mitLogo from '../assets/MIT_LOGO.png';
import chicagoBoothLogo from '../assets/CHICAGO_BOOTH_LOGO.png';
import kelloggLogo from '../assets/KELLOGG_LOGO.png';
import berkeleyHaasLogo from '../assets/BERKELEY_HAAS_LOGO.png';
import yaleSomLogo from '../assets/YALE_SOM_LOGO.png';
import dardenLogo from '../assets/DARDEN_LOGO.png';
import uclaAndersonLogo from '../assets/UCLA_ANDERSON_LOGO.png';
import emoryGoizuetaLogo from '../assets/EMORY_GOIZUETA_LOGO.png';

type EssayFeedback = {
  strengths: string[];
  improvements: string[];
  mbaAdvice: string[];
};

function safeExtractJson(text: string): string | null {
  const first = text.indexOf('{');
  const last = text.lastIndexOf('}');
  if (first === -1 || last === -1 || last <= first) return null;
  return text.slice(first, last + 1);
}

function normalizeBulletArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
    .slice(0, 6);
}

function parseEssayFeedback(rawText: string): EssayFeedback {
  const extracted = safeExtractJson(rawText);
  const parsed = extracted ? JSON.parse(extracted) : JSON.parse(rawText);

  return {
    strengths: normalizeBulletArray(parsed.strengths),
    improvements: normalizeBulletArray(parsed.improvements),
    mbaAdvice: normalizeBulletArray(parsed.mbaAdvice)
  };
}

const DEFAULT_FEEDBACK: EssayFeedback = {
  strengths: [],
  improvements: [],
  mbaAdvice: []
};

const DEFAULT_ESSAY_SLOTS = ['Essay 1', 'Essay 2', 'Essay 3', 'Essay 4', 'Essay 5'];
const DEFAULT_SCHOOLS = [
  { name: 'Harvard Business School', logoSrc: hbsLogo },
  { name: 'Stanford GSB', logoSrc: stanfordLogo },
  { name: 'Wharton', logoSrc: whartonLogo },
  { name: 'Columbia Business School', logoSrc: columbiaLogo },
  { name: 'MIT Sloan', logoSrc: mitLogo },
  { name: 'Chicago Booth', logoSrc: chicagoBoothLogo },
  { name: 'Kellogg SOM', logoSrc: kelloggLogo },
  { name: 'Berkeley Haas', logoSrc: berkeleyHaasLogo },
  { name: 'Yale SOM', logoSrc: yaleSomLogo },
  { name: 'UVA Darden', logoSrc: dardenLogo },
  { name: 'UCLA', logoSrc: uclaAndersonLogo },
  { name: 'Emory Goizueta', logoSrc: emoryGoizuetaLogo }
];

type StoredEssayDraft = {
  question: string;
  essayText: string;
  feedback: EssayFeedback;
  updatedAt: string;
};

type EssayBuilderStorage = {
  customSchools: string[];
  customSlots: string[];
  drafts: Record<string, Record<string, StoredEssayDraft>>;
};

const EMPTY_STORAGE: EssayBuilderStorage = {
  customSchools: [],
  customSlots: [],
  drafts: {}
};

const EssayBuilder: React.FC = () => {
  const { user } = useAuth();
  const [selectedSchool, setSelectedSchool] = useState(DEFAULT_SCHOOLS[0].name);
  const [selectedSlot, setSelectedSlot] = useState(DEFAULT_ESSAY_SLOTS[0]);
  const [newSchoolInput, setNewSchoolInput] = useState('');
  const [newSlotInput, setNewSlotInput] = useState('');
  const [isSchoolOverflowOpen, setIsSchoolOverflowOpen] = useState(false);
  const [isEssayOverflowOpen, setIsEssayOverflowOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [essayQuestion, setEssayQuestion] = useState('');
  const [essayText, setEssayText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [feedback, setFeedback] = useState<EssayFeedback>(DEFAULT_FEEDBACK);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [storageState, setStorageState] = useState<EssayBuilderStorage>(EMPTY_STORAGE);
  const [hasHydratedDraft, setHasHydratedDraft] = useState(false);
  const lastAutoSavedSnapshotRef = useRef<string>('');

  const schoolOptions = useMemo(() => {
    return [...DEFAULT_SCHOOLS.map((s) => s.name), ...storageState.customSchools];
  }, [storageState.customSchools]);

  const schoolLogoByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const school of DEFAULT_SCHOOLS) map.set(school.name, school.logoSrc);
    return map;
  }, []);

  const visibleSchoolCount = useMemo(() => {
    if (viewportWidth >= 1500) return 12;
    if (viewportWidth >= 1300) return 10;
    if (viewportWidth >= 1100) return 8;
    if (viewportWidth >= 900) return 7;
    if (viewportWidth >= 700) return 6;
    return 4;
  }, [viewportWidth]);

  const visibleSchoolOptions = useMemo(
    () => schoolOptions.slice(0, visibleSchoolCount),
    [schoolOptions, visibleSchoolCount]
  );
  const overflowSchoolOptions = useMemo(
    () => schoolOptions.slice(visibleSchoolCount),
    [schoolOptions, visibleSchoolCount]
  );

  const slotOptions = useMemo(() => {
    return [...DEFAULT_ESSAY_SLOTS, ...storageState.customSlots];
  }, [storageState.customSlots]);
  const visibleEssaySlots = useMemo(() => slotOptions.slice(0, 3), [slotOptions]);
  const overflowEssaySlots = useMemo(() => slotOptions.slice(3), [slotOptions]);

  const storageKey = useMemo(() => {
    return user?.id ? `deferredly.essayBuilder.v1.${user.id}` : null;
  }, [user?.id]);

  const wordCount = useMemo(() => {
    const trimmed = essayText.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [essayText]);

  const selectedEssayLabel = useMemo(() => {
    const match = selectedSlot.match(/^Essay\s*(\d+)$/i);
    if (match) return `Essay ${match[1]}`;
    return selectedSlot;
  }, [selectedSlot]);

  const selectedEssayQuestionLabel = useMemo(() => {
    const match = selectedSlot.match(/^Essay\s*(\d+)$/i);
    if (match) return `${selectedSchool} Essay Question ${match[1]}`;
    return `${selectedSchool} Essay Question — ${selectedSlot}`;
  }, [selectedSchool, selectedSlot]);

  const selectedEssayDraftLabel = useMemo(() => {
    const match = selectedSlot.match(/^Essay\s*(\d+)$/i);
    if (match) return `${selectedSchool} Essay Draft ${match[1]}`;
    return `${selectedSchool} Essay Draft — ${selectedSlot}`;
  }, [selectedSchool, selectedSlot]);

  const canAnalyze = !isAnalyzing && essayText.trim().length > 0;

  useEffect(() => {
    if (!storageKey) {
      setStorageState(EMPTY_STORAGE);
      setEssayQuestion('');
      setEssayText('');
      setFeedback(DEFAULT_FEEDBACK);
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setStorageState(EMPTY_STORAGE);
        setEssayQuestion('');
        setEssayText('');
        setFeedback(DEFAULT_FEEDBACK);
        return;
      }

      const parsed = JSON.parse(raw) as EssayBuilderStorage;
      const nextState: EssayBuilderStorage = {
        customSchools: Array.isArray(parsed.customSchools)
          ? parsed.customSchools.filter((v) => typeof v === 'string' && v.trim())
          : [],
        customSlots: Array.isArray(parsed.customSlots)
          ? parsed.customSlots.filter((v) => typeof v === 'string' && v.trim())
          : [],
        drafts: parsed.drafts && typeof parsed.drafts === 'object' ? parsed.drafts : {}
      };
      setStorageState(nextState);
      setEssayQuestion('');
      setEssayText('');
      setFeedback(DEFAULT_FEEDBACK);
    } catch {
      setStorageState(EMPTY_STORAGE);
      setEssayQuestion('');
      setEssayText('');
      setFeedback(DEFAULT_FEEDBACK);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!schoolOptions.includes(selectedSchool)) {
      setSelectedSchool(schoolOptions[0] || '');
    }
  }, [schoolOptions, selectedSchool]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!slotOptions.includes(selectedSlot)) {
      setSelectedSlot(slotOptions[0] || '');
    }
  }, [slotOptions, selectedSlot]);

  useEffect(() => {
    setSaveMessage(null);
    const draft = storageState.drafts[selectedSchool]?.[selectedSlot];
    if (!draft) {
      setEssayQuestion('');
      setEssayText('');
      setFeedback(DEFAULT_FEEDBACK);
      setHasHydratedDraft(true);
      lastAutoSavedSnapshotRef.current = JSON.stringify({
        selectedSchool,
        selectedSlot,
        essayQuestion: '',
        essayText: '',
        feedback: DEFAULT_FEEDBACK
      });
      return;
    }
    setEssayQuestion(draft.question || '');
    setEssayText(draft.essayText || '');
    setFeedback(draft.feedback || DEFAULT_FEEDBACK);
    setHasHydratedDraft(true);
    lastAutoSavedSnapshotRef.current = JSON.stringify({
      selectedSchool,
      selectedSlot,
      essayQuestion: draft.question || '',
      essayText: draft.essayText || '',
      feedback: draft.feedback || DEFAULT_FEEDBACK
    });
  }, [selectedSchool, selectedSlot, storageState.drafts]);

  const persistState = useCallback((next: EssayBuilderStorage) => {
    if (!storageKey) return;
    setStorageState(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  }, [storageKey]);

  const handleSaveDraft = () => {
    if (!user) {
      setSaveMessage('Log in to save essay drafts.');
      return;
    }

    const updated: EssayBuilderStorage = {
      ...storageState,
      drafts: {
        ...storageState.drafts,
        [selectedSchool]: {
          ...(storageState.drafts[selectedSchool] || {}),
          [selectedSlot]: {
            question: essayQuestion,
            essayText,
            feedback,
            updatedAt: new Date().toISOString()
          }
        }
      }
    };

    try {
      persistState(updated);
      setSaveMessage(`Saved ${selectedSchool} • ${selectedSlot}`);
      lastAutoSavedSnapshotRef.current = JSON.stringify({
        selectedSchool,
        selectedSlot,
        essayQuestion,
        essayText,
        feedback
      });
    } catch {
      setSaveMessage('Could not save this draft.');
    }
  };

  /** Google does not allow prefilling a Doc via URL; copy + docs.new is the standard browser-safe approach. */
  const handleOpenInGoogleDocs = useCallback(async () => {
    const text = essayText.trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      window.open('https://docs.new', '_blank', 'noopener,noreferrer');
      setSaveMessage('Draft copied — in the new tab, paste into your Google Doc (⌘V / Ctrl+V).');
    } catch {
      window.open('https://docs.new', '_blank', 'noopener,noreferrer');
      setSaveMessage(
        'Opened a new Google Doc — copy your draft from this page (⌘C / Ctrl+C) and paste there.'
      );
    }
  }, [essayText]);

  useEffect(() => {
    if (!user || !hasHydratedDraft || !storageKey) return;

    const snapshot = JSON.stringify({
      selectedSchool,
      selectedSlot,
      essayQuestion,
      essayText,
      feedback
    });

    if (snapshot === lastAutoSavedSnapshotRef.current) return;

    const timer = window.setTimeout(() => {
      try {
        const updated: EssayBuilderStorage = {
          ...storageState,
          drafts: {
            ...storageState.drafts,
            [selectedSchool]: {
              ...(storageState.drafts[selectedSchool] || {}),
              [selectedSlot]: {
                question: essayQuestion,
                essayText,
                feedback,
                updatedAt: new Date().toISOString()
              }
            }
          }
        };
        persistState(updated);
        lastAutoSavedSnapshotRef.current = snapshot;
        setSaveMessage(`Autosaved ${selectedSchool} • ${selectedSlot}`);
      } catch {
        // Ignore autosave failures; manual save still available.
      }
    }, 6000);

    return () => window.clearTimeout(timer);
  }, [
    user,
    hasHydratedDraft,
    storageKey,
    selectedSchool,
    selectedSlot,
    essayQuestion,
    essayText,
    feedback,
    storageState,
    persistState
  ]);

  /** Draft slots: [ and ] cycle (← / → reserved for main navbar). */
  useEffect(() => {
    const typingSelector =
      'textarea, input:not([type="checkbox"]):not([type="radio"]), select, [contenteditable="true"]';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'BracketLeft' && e.code !== 'BracketRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el.closest(typingSelector)) return;
      if (el.isContentEditable) return;

      if (slotOptions.length < 2) return;

      e.preventDefault();
      const idx = Math.max(0, slotOptions.indexOf(selectedSlot));
      const nextIdx =
        e.code === 'BracketRight'
          ? (idx + 1) % slotOptions.length
          : (idx - 1 + slotOptions.length) % slotOptions.length;
      setSelectedSlot(slotOptions[nextIdx]);
      setIsEssayOverflowOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [slotOptions, selectedSlot]);

  const handleAddSchool = () => {
    const nextSchool = newSchoolInput.trim();
    if (!nextSchool) return;
    if (schoolOptions.includes(nextSchool)) {
      setSelectedSchool(nextSchool);
      setNewSchoolInput('');
      return;
    }
    const nextState = {
      ...storageState,
      customSchools: [...storageState.customSchools, nextSchool]
    };
    persistState(nextState);
    setSelectedSchool(nextSchool);
    setIsSchoolOverflowOpen(false);
    setNewSchoolInput('');
  };

  const handleAddSlot = () => {
    const nextSlot = newSlotInput.trim();
    if (!nextSlot) return;
    if (slotOptions.includes(nextSlot)) {
      setSelectedSlot(nextSlot);
      setNewSlotInput('');
      return;
    }
    const nextState = {
      ...storageState,
      customSlots: [...storageState.customSlots, nextSlot]
    };
    persistState(nextState);
    setSelectedSlot(nextSlot);
    setIsEssayOverflowOpen(false);
    setNewSlotInput('');
  };

  const analyzeEssay = async () => {
    const trimmedEssay = essayText.trim();
    if (!trimmedEssay) {
      setError('Add your essay text in the draft before analyzing.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const completionText = await fetchOpenAIChatCompletion({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: `You are a top MBA admissions essay coach.
You must provide concise, high-quality FEEDBACK ONLY.
Do not rewrite, paraphrase, or draft essay sections.
Do not provide replacement paragraphs or sentence rewrites.
Return only bullet-style feedback tailored to MBA applications.

Return strict JSON with exactly these keys:
{
  "strengths": string[],
  "improvements": string[],
  "mbaAdvice": string[]
}

Rules:
- 3-5 bullets per array.
- Keep each bullet practical and specific.
- improvements should focus on what to strengthen (clarity, impact, reflection, structure, evidence).
- mbaAdvice should include admissions-oriented guidance (leadership, goals, fit, authenticity, differentiation).
- No markdown code fences, no extra keys, no commentary outside JSON.`
          },
          {
            role: 'user',
            content: `Analyze the MBA essay below and return feedback bullets only.

Essay question / prompt:
"""
${essayQuestion.trim() || 'Not provided'}
"""

Essay:
"""
${trimmedEssay}
"""`
          }
        ],
        max_tokens: 700,
        temperature: 0.35
      });

      const parsed = parseEssayFeedback(completionText);
      setFeedback(parsed);
    } catch (err) {
      setError('Could not analyze this essay right now. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="essay-builder-page">
      <div className="essay-builder-container">
        <header className="essay-builder-header">
          <h1 className="essay-builder-title">Essay Analysis</h1>
          <p className="essay-builder-subtitle">
            Draft on the left, then run an MBA-focused analysis on the right. Feedback is bullets only.
          </p>
        </header>

        {!user && (
          <div className="essay-auth-callout" role="status">
            <p>
              You can analyze essays without an account.&nbsp;
              <strong>Log in</strong> to save drafts across devices:&nbsp;
              <Link to="/login">Log in</Link> or <Link to="/signup">sign up</Link>.
            </p>
          </div>
        )}
        <section className="essay-organizer-panel">
          <div className="essay-organizer-row essay-organizer-row--school">
            <div className="essay-organizer-field">
              <label>School</label>
              <div className="essay-school-picker" role="group" aria-label="Select school">
                {visibleSchoolOptions.map((school) => {
                  const logoSrc = schoolLogoByName.get(school);
                  return (
                    <button
                      key={school}
                      type="button"
                      className={`essay-school-button ${selectedSchool === school ? 'active' : ''}`}
                      title={school}
                      onClick={() => {
                        setSelectedSchool(school);
                        setIsSchoolOverflowOpen(false);
                      }}
                    >
                      {logoSrc ? (
                        <img src={logoSrc} alt="" className="essay-school-button-logo" />
                      ) : (
                        <span className="essay-school-button-text">{school}</span>
                      )}
                    </button>
                  );
                })}

                <div className="essay-school-overflow">
                  <button
                    type="button"
                    className={`essay-school-button essay-school-overflow-trigger ${isSchoolOverflowOpen ? 'active' : ''}`}
                    onClick={() => setIsSchoolOverflowOpen((v) => !v)}
                    aria-label="More schools"
                  >
                    ...
                  </button>
                  {isSchoolOverflowOpen && (
                    <div className="essay-school-overflow-menu">
                      {overflowSchoolOptions.map((school) => {
                        const logoSrc = schoolLogoByName.get(school);
                        return (
                          <button
                            key={school}
                            type="button"
                            className={`essay-school-overflow-item ${selectedSchool === school ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedSchool(school);
                              setIsSchoolOverflowOpen(false);
                            }}
                          >
                            {logoSrc ? (
                              <img src={logoSrc} alt="" className="essay-school-overflow-logo" />
                            ) : (
                              <span className="essay-school-overflow-fallback" />
                            )}
                            <span>{school}</span>
                          </button>
                        );
                      })}
                      <div className="essay-overflow-add-row">
                        <input
                          type="text"
                          value={newSchoolInput}
                          onChange={(e) => setNewSchoolInput(e.target.value)}
                          placeholder="Add school..."
                        />
                        <button type="button" className="essay-overflow-add-button" onClick={handleAddSchool}>
                          Add School
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="essay-selected-school" aria-live="polite">
                Selected: <strong>{selectedSchool}</strong>
              </div>
            </div>

            <div className="essay-organizer-field essay-organizer-field--essay-buttons">
              <label>Essay Draft</label>
              <div className="essay-slot-picker" role="group" aria-label="Select essay draft">
                {visibleEssaySlots.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    className={`essay-slot-button ${selectedSlot === slot ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedSlot(slot);
                      setIsEssayOverflowOpen(false);
                    }}
                  >
                    {slot}
                  </button>
                ))}

                <div className="essay-slot-overflow">
                  <button
                    type="button"
                    className={`essay-slot-button essay-slot-overflow-trigger ${isEssayOverflowOpen ? 'active' : ''}`}
                    onClick={() => setIsEssayOverflowOpen((v) => !v)}
                    aria-label="More essay drafts"
                  >
                    ...
                  </button>
                  {isEssayOverflowOpen && (
                    <div className="essay-slot-overflow-menu">
                      {overflowEssaySlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          className={`essay-slot-overflow-item ${selectedSlot === slot ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedSlot(slot);
                            setIsEssayOverflowOpen(false);
                          }}
                        >
                          {slot}
                        </button>
                      ))}
                      <div className="essay-overflow-add-row">
                        <input
                          type="text"
                          value={newSlotInput}
                          onChange={(e) => setNewSlotInput(e.target.value)}
                          placeholder="Add essay prompt..."
                        />
                        <button type="button" className="essay-overflow-add-button" onClick={handleAddSlot}>
                          Add Essay Prompt
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="essay-selected-slot" aria-live="polite">
                Selected: <strong>{selectedEssayLabel}</strong>
              </div>
            </div>
          </div>

          {saveMessage && <p className="essay-save-message">{saveMessage}</p>}
        </section>

        <section className="essay-question-panel">
          <div className="essay-panel-header">
            <h2>{selectedEssayQuestionLabel}</h2>
          </div>
          <textarea
            className="essay-question-input"
            placeholder={`Paste the exact school prompt for ${selectedEssayLabel} here...`}
            value={essayQuestion}
            onChange={(e) => setEssayQuestion(e.target.value)}
          />
          <p className="essay-input-hint">
            Including the exact prompt helps the analysis check whether your essay directly answers the question.
          </p>
        </section>

        <section className="essay-builder-grid">
          <div className="essay-editor-panel">
            <div className="essay-panel-header">
              <h2>{selectedEssayDraftLabel}</h2>
              <div className="essay-draft-header-actions">
                <span className="essay-word-count">{wordCount} words</span>
                <button
                  type="button"
                  className="essay-secondary-button essay-google-drive-button"
                  onClick={() => void handleOpenInGoogleDocs()}
                  disabled={!essayText.trim()}
                  title="Copies your draft and opens a blank Google Doc (paste once). Full auto-upload requires Google sign-in via the Docs API."
                >
                  <svg
                    className="essay-google-drive-icon"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                    focusable="false"
                  >
                    <path
                      fill="#4285F4"
                      d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.005-.02-1.708-3.001-3.775-6.62l-3.76-6.574zm-4.76 1.73a789.828 789.861 0 0 0-3.63 6.319L0 15.868l1.89 3.298 1.885 3.297 3.62-6.335 3.618-6.33-1.88-3.287C8.1 4.704 7.255 3.22 7.25 3.214zm2.259 12.653-.203.348c-.114.198-.96 1.672-1.88 3.287a423.93 423.948 0 0 1-1.698 2.97c-.01.026 3.24.042 7.222.042h7.244l1.796-3.157c.992-1.734 1.85-3.23 1.906-3.323l.104-.167h-7.249z"
                    />
                  </svg>
                  Google Docs
                </button>
                <button
                  type="button"
                  className="essay-secondary-button"
                  onClick={handleSaveDraft}
                  disabled={!user}
                  title={!user ? 'Log in to save drafts' : undefined}
                >
                  Save Draft
                </button>
              </div>
            </div>
            <textarea
              className="essay-input"
              placeholder={`Write ${selectedEssayLabel} here...`}
              value={essayText}
              onChange={(e) => setEssayText(e.target.value)}
            />
            <p className="essay-input-hint">
              Tip: include concrete examples, outcomes, and your career goals for better feedback quality.
            </p>
          </div>

          <div className="essay-analysis-panel">
            <div className="essay-panel-header">
              <h2>Analyze Response</h2>
              <button
                type="button"
                className="essay-analyze-button"
                onClick={analyzeEssay}
                disabled={!canAnalyze}
                title={!essayText.trim() ? 'Write something in the draft to analyze' : undefined}
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze Response'}
              </button>
            </div>

            {error && (
              <div className="essay-feedback-error" role="alert">
                {error}
              </div>
            )}

            {!error &&
              !isAnalyzing &&
              feedback.strengths.length === 0 &&
              feedback.improvements.length === 0 &&
              feedback.mbaAdvice.length === 0 && (
                <div className="essay-feedback-empty">
                  Your analysis will appear here with strengths, improvement areas, and MBA-specific advice.
                </div>
              )}

            {isAnalyzing && <div className="essay-feedback-loading">Generating feedback...</div>}

            {(feedback.strengths.length > 0 ||
              feedback.improvements.length > 0 ||
              feedback.mbaAdvice.length > 0) && (
              <div className="essay-feedback-sections">
                <section className="essay-feedback-card">
                  <h3>What your essay does well</h3>
                  <ul>
                    {feedback.strengths.map((item, idx) => (
                      <li key={`strength-${idx}`}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section className="essay-feedback-card">
                  <h3>How to improve</h3>
                  <ul>
                    {feedback.improvements.map((item, idx) => (
                      <li key={`improve-${idx}`}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section className="essay-feedback-card">
                  <h3>MBA application advice</h3>
                  <ul>
                    {feedback.mbaAdvice.map((item, idx) => (
                      <li key={`mba-${idx}`}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default EssayBuilder;
